import { useEffect, useState } from "react";
import { Plus, Trash2, BookOpen, UserCheck, CheckSquare } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function AdminMaterias() {
  const { user } = useAuth();
  const esAdmin = user?.rol === "admin";
  const [rows, setRows] = useState(null);
  const [docentes, setDocentes] = useState([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [docenteMasivo, setDocenteMasivo] = useState("");
  const [form, setForm] = useState({ nombre: "", grado: "5to Primaria", docente_id: "" });

  const cargar = () => {
    api.get("/materias").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/usuarios", { params: { rol: "docente" } }).then((r) => setDocentes(r.data));
  };

  useEffect(cargar, []);

  const crear = async () => {
    if (!form.nombre.trim()) return toast.error("Indique el nombre de la materia");
    try {
      await api.post("/materias", { ...form, docente_id: form.docente_id || null });
      toast.success("Materia creada");
      setOpen(false);
      setForm({ nombre: "", grado: "5to Primaria", docente_id: "" });
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const asignar = async (m, docente_id) => {
    try {
      await api.put(`/materias/${m.id}/docente`, { docente_id: docente_id || null });
      toast.success(docente_id ? "Docente asignado a la materia" : "Materia sin docente");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const asignarMasivo = async () => {
    try {
      const { data } = await api.put("/materias/asignar-docente", {
        materia_ids: sel, docente_id: docenteMasivo || null,
      });
      toast.success(data.docente
        ? `${data.actualizadas} materia(s) asignada(s) a ${data.docente}`
        : `${data.actualizadas} materia(s) quedaron sin docente`);
      setSel([]);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async (m) => {
    try {
      await api.delete(`/materias/${m.id}`);
      toast.success("Materia eliminada");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!rows) return <Loading />;

  const grados = [...new Set(rows.map((r) => r.grado))];

  return (
    <div data-testid="admin-materias">
      <PageTitle title="Materias y docentes"
                 subtitle="Asigne a cada materia el docente responsable de publicar notas y actividades.">
        <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="nueva-materia">
          <Plus className="mr-2 h-4 w-4" /> Nueva materia
        </Button>
      </PageTitle>

      {rows.length === 0 && <Empty testId="materias-vacias">Aún no hay materias registradas.</Empty>}

      <section className="sticky top-2 z-20 mb-8 rounded-lg border border-border/60 bg-card/95 p-5 backdrop-blur"
               data-testid="asignador-masivo">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[220px] flex-1 space-y-2">
            <Label className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
              <UserCheck className="h-3 w-3" /> Asignación rápida
            </Label>
            <select value={docenteMasivo} onChange={(e) => setDocenteMasivo(e.target.value)}
                    data-testid="docente-masivo"
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
              <option value="">Quitar docente (sin asignar)</option>
              {docentes.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
            </select>
          </div>
          <Button className="rounded-full" disabled={sel.length === 0} onClick={asignarMasivo}
                  data-testid="aplicar-asignacion-masiva">
            Asignar a {sel.length} materia(s)
          </Button>
          {sel.length > 0 && (
            <Button variant="ghost" className="rounded-full" onClick={() => setSel([])}
                    data-testid="limpiar-seleccion">Limpiar selección</Button>
          )}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Marque las materias que quiera cambiar (o use "Seleccionar todo" en cada grado), elija el docente y aplique.
        </p>
      </section>

      {grados.map((g) => (
        <section key={g} className="mb-10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg font-semibold">{g}</h2>
            <Button size="sm" variant="outline" className="rounded-full" data-testid={`seleccionar-todo-${g}`}
                    onClick={() => {
                      const ids = rows.filter((r) => r.grado === g).map((r) => r.id);
                      const todas = ids.every((id) => sel.includes(id));
                      setSel(todas ? sel.filter((id) => !ids.includes(id))
                                   : [...new Set([...sel, ...ids])]);
                    }}>
              <CheckSquare className="mr-1 h-3 w-3" /> Seleccionar todo
            </Button>
          </div>
          <div className="mt-4 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid={`materias-${g}`}>
            {rows.filter((r) => r.grado === g).map((m) => (
              <article key={m.id}
                       className={`lift rounded-lg border bg-card p-6 ${
                         sel.includes(m.id) ? "border-primary ring-1 ring-primary/40" : "border-border/60"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 text-primary">
                    <input type="checkbox" checked={sel.includes(m.id)} data-testid={`seleccionar-materia-${m.id}`}
                           onChange={() => setSel(sel.includes(m.id)
                             ? sel.filter((id) => id !== m.id) : [...sel, m.id])}
                           className="h-4 w-4 accent-current" />
                    <BookOpen className="h-4 w-4" />
                    <h3 className="font-display text-base font-semibold">{m.nombre}</h3>
                  </div>
                  <Badge variant={m.docente_id ? "secondary" : "outline"}>
                    {m.docente_id ? "asignada" : "sin docente"}
                  </Badge>
                </div>
                <div className="mt-5 space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Docente</Label>
                  <select value={m.docente_id || ""} onChange={(e) => asignar(m, e.target.value)}
                          data-testid={`asignar-docente-${m.id}`}
                          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                    <option value="">Sin asignar</option>
                    {docentes.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
                  </select>
                </div>
                {esAdmin && (
                  <Button size="sm" variant="ghost" className="mt-4" onClick={() => eliminar(m)}
                          data-testid={`eliminar-materia-${m.id}`}>
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                )}
              </article>
            ))}
          </div>
        </section>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="modal-materia">
          <DialogHeader>
            <DialogTitle className="font-display">Nueva materia</DialogTitle>
            <DialogDescription>Las materias se muestran a los estudiantes del grado indicado.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                     data-testid="materia-nombre" />
            </div>
            <div className="space-y-2">
              <Label>Grado</Label>
              <Input value={form.grado} onChange={(e) => setForm({ ...form, grado: e.target.value })}
                     data-testid="materia-grado" />
            </div>
            <div className="space-y-2">
              <Label>Docente</Label>
              <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                      value={form.docente_id} onChange={(e) => setForm({ ...form, docente_id: e.target.value })}
                      data-testid="materia-docente">
                <option value="">Sin asignar</option>
                {docentes.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
              </select>
            </div>
            <Button className="rounded-full py-6" onClick={crear} data-testid="guardar-materia">Crear materia</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
